import React, { useEffect, useMemo, useState } from 'react';
import {
  addDays,
  addWeeks,
  endOfDay,
  format,
  getISOWeek,
  isSameDay,
  isWithinInterval,
  startOfDay,
  startOfWeek,
  subWeeks
} from 'date-fns';
import { fi } from 'date-fns/locale';
import { useDispatch, useSelector } from 'react-redux';
import { clearHolidayAlerts, createHoliday, fetchHolidays, removeHoliday, updateHoliday } from '../redux/actions/thunks/Holidays';
import HolidayModal from './HolidayModal';

const isWeekend = (date) => {
  const day = date.getDay();
  return day === 0 || day === 6;
};

const HolidaysPage = () => {
  const [person, setPerson] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [formError, setFormError] = useState('');
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [weeksToShow, setWeeksToShow] = useState(() => {
    const saved = window.localStorage.getItem('weeksToShow');
    return saved ? Number(saved) : 4;
  });
  const [viewStartDate, setViewStartDate] = useState(() => {
    const saved = window.localStorage.getItem('viewStartDate');
    return saved ? new Date(saved) : startOfWeek(new Date(), { weekStartsOn: 1 });
  });

  const dispatch = useDispatch();
  const { items: holidays, loading, error, success } = useSelector((state) => state.holidays);

  useEffect(() => {
    dispatch(fetchHolidays());
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      setPerson('');
      setStartDate('');
      setEndDate('');
      setFormError('');
      setSelectedHoliday(null);
      setIsSaving(false);
      setIsDeleting(false);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      setIsSaving(false);
      setIsDeleting(false);
    }
  }, [error]);

  useEffect(() => {
    window.localStorage.setItem('weeksToShow', String(weeksToShow));
  }, [weeksToShow]);

  useEffect(() => {
    window.localStorage.setItem('viewStartDate', viewStartDate.toISOString());
  }, [viewStartDate]);

  const holidaysByPerson = useMemo(() => {
    return holidays.reduce((acc, holiday) => {
      if (!acc[holiday.person]) {
        acc[holiday.person] = [];
      }
      acc[holiday.person].push(holiday);
      return acc;
    }, {});
  }, [holidays]);

  const calendarDates = useMemo(() => {
    return Array.from({ length: weeksToShow * 7 }, (_, index) => addDays(viewStartDate, index));
  }, [viewStartDate, weeksToShow]);

  const DAY_COLUMN_MIN_WIDTH = 60;

  const dayColumnTemplate = useMemo(
    () => ({ gridTemplateColumns: `repeat(${calendarDates.length}, minmax(${DAY_COLUMN_MIN_WIDTH}px, 1fr))` }),
    [calendarDates.length]
  );

  const weekLabels = useMemo(
    () =>
      Array.from({ length: weeksToShow }, (_, weekIndex) => {
        const weekStart = addWeeks(viewStartDate, weekIndex);
        return { key: weekStart.toISOString(), number: getISOWeek(weekStart) };
      }),
    [viewStartDate, weeksToShow]
  );

  const monthLabels = useMemo(() => {
    if (calendarDates.length === 0) return [];

    const labels = [];
    let currentMonth = calendarDates[0].getMonth();
    let currentYear = calendarDates[0].getFullYear();
    let span = 0;

    calendarDates.forEach((date) => {
      if (date.getMonth() === currentMonth && date.getFullYear() === currentYear) {
        span += 1;
      } else {
        labels.push({
          key: `${currentYear}-${currentMonth}`,
          label: format(new Date(currentYear, currentMonth, 1), 'LLLL yyyy', { locale: fi }),
          span
        });

        currentMonth = date.getMonth();
        currentYear = date.getFullYear();
        span = 1;
      }
    });

    labels.push({
      key: `${currentYear}-${currentMonth}`,
      label: format(new Date(currentYear, currentMonth, 1), 'LLLL yyyy', { locale: fi }),
      span
    });

    return labels;
  }, [calendarDates]);

  const handleSubmit = (event) => {
    event.preventDefault();
    setFormError('');
    dispatch(clearHolidayAlerts());

    if (!person || !startDate || !endDate) {
      setFormError('Lisää henkilö sekä loman alku- ja loppupäivä.');
      return;
    }

    setIsSaving(true);
    dispatch(
      createHoliday({
        person,
        startDate,
        endDate
      })
    );
  };

  const handleUpdate = (payload) => {
    if (!selectedHoliday) return;
    setIsSaving(true);
    dispatch(updateHoliday(selectedHoliday._id, payload));
  };

  const handleDelete = (holidayId) => {
    setIsDeleting(true);
    dispatch(clearHolidayAlerts());
    dispatch(removeHoliday(holidayId));
  };

  const findHolidayForDate = (personName, date) => {
    return holidaysByPerson[personName]?.find((holiday) =>
      isWithinInterval(date, {
        start: startOfDay(new Date(holiday.startDate)),
        end: endOfDay(new Date(holiday.endDate))
      })
    );
  };

  const handleCloseModal = () => {
    setSelectedHoliday(null);
    setIsSaving(false);
    setIsDeleting(false);
  };

  const goToPreviousWeeks = () => {
    setViewStartDate((current) => subWeeks(current, 1));
  };

  const goToNextWeeks = () => {
    setViewStartDate((current) => addWeeks(current, 1));
  };

  return (
    <div className="holiday-page light">
      <div className="holiday-hero">
        <form className="holiday-form" onSubmit={handleSubmit}>
          <div className="form-row">
            <label htmlFor="person">Henkilö</label>
            <input
              id="person"
              value={person}
              onChange={(e) => setPerson(e.target.value)}
              placeholder="Nimi"
            />
          </div>
          <div className="form-grid">
            <div className="form-row">
              <label htmlFor="start">Alkaa</label>
              <input
                id="start"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="form-row">
              <label htmlFor="end">Päättyy</label>
              <input
                id="end"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
          <button type="submit" className="holiday-primary-button" disabled={loading}>
            Lisää lomapäivät
          </button>
          {formError && <p className="form-message error">{formError}</p>}
          {error && <p className="form-message error">{error}</p>}
          {success && <p className="form-message success">{success}</p>}
        </form>
        <div>
          <p className="eyebrow">Lomakalenteri</p>

          <div className="calendar-controls">
            <div className="control-group">
              <p className="control-label">Näytettävät viikot</p>
              <input
                type="range"
                min="3"
                max="12"
                value={weeksToShow}
                onChange={(e) => setWeeksToShow(Number(e.target.value))}
              />
              <span className="control-value">{weeksToShow} viikkoa</span>
            </div>
            <div className="control-buttons">
              <button className="ghost-button" type="button" onClick={goToPreviousWeeks}>
                ◀ Edelliset
              </button>
              <button className="ghost-button" type="button" onClick={() => setViewStartDate(startOfWeek(new Date(), { weekStartsOn: 1 }))}>
                Nykyinen viikko
              </button>
              <button className="ghost-button" type="button" onClick={goToNextWeeks}>
                Seuraavat ▶
              </button>
            </div>
          </div>
        </div>


      </div>

      <div className="holiday-calendar">
        <div className="calendar-scroll">
          <div className="holiday-calendar__header">
            <div className="person-column">Henkilö</div>
            <div className="calendar-grid">
              <div className="month-row" style={dayColumnTemplate}>
                {monthLabels.map((month) => (
                  <div key={month.key} className="month-label" style={{ gridColumn: `span ${month.span}` }}>
                    {month.label}
                  </div>
                ))}
              </div>
              <div className="week-row" style={dayColumnTemplate}>
                {weekLabels.map((week) => (
                  <div key={week.key} className="week-label" style={{ gridColumn: 'span 7' }}>
                    Vko {week.number}
                  </div>
                ))}
              </div>
              <div className="days-row" style={dayColumnTemplate}>
                {calendarDates.map((date) => (
                  <div
                    key={date.toISOString()}
                    className={[
                      'day-label',
                      isWeekend(date) ? 'day-label--weekend' : '',
                      isSameDay(date, new Date()) ? 'day-label--today' : ''
                    ].filter(Boolean).join(' ')}
                  >
                    <span>{format(date, 'EEEEEE', { locale: fi })}</span>
                    <span>{format(date, 'd.M.', { locale: fi })}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="holiday-calendar__body">
            {Object.keys(holidaysByPerson).length === 0 && !loading && (
              <div className="holiday-card empty-state">
                <p>Ei lomia lisättynä. Lisää ensimmäinen lomajakso yllä olevalla lomakkeella.</p>
              </div>
            )}

            {loading && <div className="holiday-card">Ladataan lomia...</div>}

            {Object.keys(holidaysByPerson).map((name) => (
              <div key={name} className="holiday-row">
                <div className="person-column">{name}</div>
                <div className="days-row" style={dayColumnTemplate}>
                  {calendarDates.map((date) => {
                    const holiday = findHolidayForDate(name, date);
                    const holidayStart = holiday ? startOfDay(new Date(holiday.startDate)) : null;
                    const holidayEnd = holiday ? endOfDay(new Date(holiday.endDate)) : null;
                    const isStart = holiday && isSameDay(date, holidayStart);
                    const isEnd = holiday && isSameDay(date, holidayEnd);

                    const cellClasses = [
                      'holiday-cell',
                      holiday ? 'holiday-cell--active' : '',
                      isStart ? 'holiday-cell--start' : '',
                      isEnd ? 'holiday-cell--end' : '',
                      isWeekend(date) ? 'holiday-cell--weekend' : '',
                      isSameDay(date, new Date()) ? 'holiday-cell--today' : ''
                    ]
                      .filter(Boolean)
                      .join(' ');

                    const holidayRangeLabel =
                      holidayStart && holidayEnd ? `${format(holidayStart, 'd.M.')} - ${format(holidayEnd, 'd.M.')}` : '';

                    return (
                      <button
                        key={date.toISOString()}
                        type="button"
                        className={cellClasses}
                        onClick={() => holiday && setSelectedHoliday(holiday)}
                      >
                        {isStart && <span className="pill-label">{holidayRangeLabel}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {selectedHoliday && (
        <HolidayModal
          holiday={selectedHoliday}
          onClose={handleCloseModal}
          onSave={handleUpdate}
          onDelete={handleDelete}
          saving={isSaving}
          deleting={isDeleting}
        />
      )}
    </div>
  );
};

export default HolidaysPage;
